import taskManagerApi from '@/features/shared/services/taskManagerApi';
/**
 * Fetch all attachments.
 */
export async function fetchAttachments() {
    try {
        const response = await taskManagerApi.get('/attachments');
        return response.data;
    }
    catch (error) {
        handleAttachmentError(error, "récupération des pièces jointes");
    }
}
/**
 * Upload a single file (expects FormData with key "attachment").
 */
export async function uploadAttachment(formData) {
    try {
        const response = await taskManagerApi.post('/attachments/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data.data;
    }
    catch (error) {
        handleAttachmentError(error, "upload de la pièce jointe");
    }
}
/**
 * Upload multiple files (expects FormData with key "attachments").
 */
export async function uploadMultipleAttachments(formData) {
    try {
        const response = await taskManagerApi.post('/attachments/multiple-upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data.data;
    }
    catch (error) {
        handleAttachmentError(error, "upload multiple des pièces jointes");
    }
}
/**
 * Download a file by its ID (returns a Blob).
 */
export async function downloadAttachment(id) {
    try {
        const response = await taskManagerApi.get(`/attachments/download/${id}`, {
            responseType: 'blob',
        });
        const contentType = response.headers['content-type'];
        const contentDisposition = response.headers['content-disposition'];
        const fallbackType = contentDisposition ? inferMimeTypeFromDisposition(contentDisposition) : undefined;
        return new Blob([response.data], {
            // Some servers send images with `application/octet-stream` + `X-Content-Type-Options: nosniff`.
            // Explicitly setting the MIME type avoids browsers refusing to display the blob inline.
            type: contentType && contentType !== 'application/octet-stream'
                ? contentType
                : fallbackType ?? response.data.type ?? 'application/octet-stream',
        });
    }
    catch (error) {
        handleAttachmentError(error, "téléchargement de la pièce jointe");
    }
}
/**
 * Build a direct URL to the attachment download endpoint.
 * Prefer using this URL in <img src> to avoid blob: URLs (CSP friendly).
 */
export function getAttachmentUrl(relativeFilePath) {
    const base = taskManagerApi.defaults.baseURL ?? '';
    // Ensure absolute URL using WHATWG URL API
    return new URL(`${relativeFilePath}`, base).toString();
}
/**
 * Build a direct URL to the attachment download endpoint.
 * Prefer using this URL in <img src> to avoid blob: URLs (CSP friendly).
 */
export function getAttachmentUrlById(id) {
    const base = taskManagerApi.defaults.baseURL ?? '';
    return new URL(`/attachments/download/${id}`, base).toString();
}
function inferMimeTypeFromDisposition(contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const filename = filenameMatch?.[1] ?? filenameMatch?.[2];
    const extension = filename?.split('.').pop()?.toLowerCase();
    switch (extension) {
        case 'png':
            return 'image/png';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'webp':
            return 'image/webp';
        case 'gif':
            return 'image/gif';
        default:
            return undefined;
    }
}
export async function deleteAttachment(id) {
    try {
        await taskManagerApi.delete(`/attachments/${id}`);
    }
    catch (error) {
        handleAttachmentError(error, "Erreur lors de la suppression du fichier");
    }
}
/**
 * Helper to standardize error handling
 */
function handleAttachmentError(error, context) {
    if (error instanceof Error) {
        const serverError = error?.response?.data?.error;
        throw new Error(serverError?.details ?? serverError?.message ?? error.message);
    }
    else {
        throw new Error(`Erreur inconnue lors de ${context}`);
    }
}
